import {Injectable} from '@angular/core';
import {IConversation} from '../../models/conversation.model';

export type ConversationExportFormat = 'csv' | 'json';

export interface ConversationDownload {
  url: string;
  fileName: string;
}

interface ConversationExportFile {
  fileName: string;
  mimeType: string;
  content: string;
}

/** Allowlisted analysis data only; never serialize the complete stored conversation. */
export function buildConversationExport(
  conversations: readonly IConversation[],
  clientId: string,
  chatbotNames: Readonly<Record<string, string>>,
  format: ConversationExportFormat,
  exportedAt = new Date()
): ConversationExportFile {
  if (!clientId?.trim() || conversations.some(conversation => conversation.clientId !== clientId)) {
    throw new Error('Reload conversations for the selected client before exporting.');
  }

  const data = conversations.map(conversation => ({
    id: conversation.id,
    chatbotId: conversation.shopAssistantId,
    chatbotName: chatbotNames[conversation.shopAssistantId] || conversation.shopAssistantId,
    createdUtc: utc(conversation.created),
    lastModifiedUtc: utc(conversation.lastModified),
    category: conversation.category?.trim() || 'Uncategorized',
    tags: conversation.tags || [],
    thumbUp: !!conversation.thumbUp,
    thumbDown: !!conversation.thumbDown,
    approved: !!conversation.approved,
    dismissed: !!conversation.dismissed,
    messages: (conversation.messages || []).map((message, index) => ({
      index: index + 1,
      timestampUtc: utc(message.timestampUtc),
      role: message.human ? 'human' : message.user ? 'user' : 'assistant',
      text: message.text || ''
    }))
  }));
  const exportedAtUtc = exportedAt.toISOString();
  const fileName = `conversations-${exportedAtUtc.replace(/[:.]/g, '-')}.${format}`;
  if (format === 'json') {
    return {
      fileName,
      mimeType: 'application/json;charset=utf-8',
      content: JSON.stringify({schemaVersion: 1, exportedAtUtc, clientId, conversationCount: data.length, conversations: data}, null, 2)
    };
  }

  const rows: unknown[][] = [[
    'conversation_id', 'chatbot_id', 'chatbot_name', 'created_utc', 'last_modified_utc',
    'category', 'tags', 'thumb_up', 'thumb_down', 'approved', 'dismissed',
    'message_index', 'message_timestamp_utc', 'role', 'message_text'
  ]];
  for (const conversation of data) {
    for (const message of conversation.messages) {
      rows.push([
        conversation.id, conversation.chatbotId, conversation.chatbotName,
        conversation.createdUtc, conversation.lastModifiedUtc, conversation.category,
        conversation.tags.join(' | '), conversation.thumbUp, conversation.thumbDown,
        conversation.approved, conversation.dismissed, message.index, message.timestampUtc,
        message.role, message.text
      ]);
    }
  }
  return {
    fileName,
    mimeType: 'text/csv;charset=utf-8',
    // BOM preserves Norwegian characters in Excel; semicolon suits Norwegian Excel defaults.
    content: '\uFEFF' + rows.map(row => row.map(csvCell).join(';')).join('\r\n') + '\r\n'
  };
}

function utc(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function csvCell(value: unknown): string {
  let text = value == null ? '' : String(value);
  // Quoting alone does not stop spreadsheets evaluating user-controlled formulas.
  let start = 0;
  while (start < text.length && (text.charCodeAt(start) < 32 || text[start].trim() === '')) start++;
  if (/^[=+\-@]/.test(text.slice(start)) || /^[\t\r\n]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}

@Injectable({providedIn: 'root'})
export class ConversationExportService {
  createDownload(conversations: readonly IConversation[], clientId: string,
                 chatbotNames: Readonly<Record<string, string>>, format: ConversationExportFormat): ConversationDownload {
    const file = buildConversationExport(conversations, clientId, chatbotNames, format);
    const url = URL.createObjectURL(new Blob([file.content], {type: file.mimeType}));
    return {url, fileName: file.fileName};
  }

  release(download: ConversationDownload): void {
    // Keep the URL available while the browser handles the user's real download-link click.
    setTimeout(() => URL.revokeObjectURL(download.url), 60_000);
  }
}
